import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Home, LogOut, ShieldCheck, Settings as SettingsIcon,
  ClipboardList, Bed, Menu, X, Sun, Moon, Zap,
  Upload, FileSpreadsheet, Users, UserPlus, Briefcase, Building2,
  Ban, Wallet, Award, TrendingUp, Crown, MapPin, Bell, MessageSquare,
  CarTaxiFront, CalendarCheck, Handshake, Utensils, Shirt,
  ShoppingCart, Tag, Link2, Truck, Ruler, Warehouse, Landmark, FileBarChart,
  Store, Package, Clock, Receipt, Settings2, Bike, LayoutDashboard, UserCog, Percent, Inbox, LifeBuoy,
  Megaphone, Radio, Building2 as BuildingIcon, Ticket as TicketIcon, FolderArchive, Search,
  FileSignature,
  Map,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { authApi, hostelNotificationsApi } from "@/lib/api";
import { setAccessLimits } from "@/lib/access";
import { useTheme } from "@/theme/ThemeProvider";
import NeurohamWidget from "@/components/NeurohamWidget";
import { toast } from "sonner";
import GlobalSearch from "@/components/GlobalSearch";
import PwaInstallButton from "@/components/PwaInstallButton";
import DebtorBlockGate from "@/components/DebtorBlockGate";

// ─── Module sub-nav definitions ──────────────────────────────────────────────

export const TABEL_SUBNAV = [
  { to: "/manager/dashboard", label: "Обзор руководителя", icon: LayoutDashboard, color: "#22c55e", testid: "nav-manager-dashboard" },
  { to: "/upload",        label: "Загрузить табель",  icon: Upload,          color: "#f97316", testid: "nav-upload" },
  { to: "/dashboard",     label: "Все табели",         icon: ClipboardList,    color: "#64748b", testid: "nav-dashboard" },
  { to: "/export",        label: "Экспорт / Импорт",  icon: FileSpreadsheet, color: "#10b981", testid: "nav-export" },
  { to: "/import",        label: "Из сообщений",       icon: MessageSquare,    color: "#ec4899", testid: "nav-import" },
  { to: "/employees",     label: "Сотрудники",         icon: Users,           color: "#3b82f6", testid: "nav-employees" },
  { to: "/candidates",    label: "Кандидаты",          icon: UserPlus,        color: "#8b5cf6", testid: "nav-candidates" },
  { to: "/professions",   label: "Профессии",          icon: Briefcase,       color: "#f59e0b", testid: "nav-professions" },
  { to: "/enterprises",   label: "Площадки",            icon: Building2,       color: "#06b6d4", testid: "nav-enterprises" },
  { to: "/fines",         label: "Штрафы",             icon: Ban,             color: "#ef4444", testid: "nav-fines" },
  { to: "/deductions",    label: "Вычеты",             icon: Wallet,          color: "#f97316", testid: "nav-deductions" },
  { to: "/bonuses",       label: "Премии",             icon: Award,           color: "#10b981", testid: "nav-bonuses" },
  { to: "/rentability",   label: "Рентабельность",     icon: TrendingUp,      color: "#6366f1", testid: "nav-rentability" },
  { to: "/daily-report",  label: "Ежедневный отчёт",   icon: CalendarCheck,   color: "#0ea5e9", testid: "nav-daily-report" },
  { to: "/audit",         label: "Аудит",             icon: ShieldCheck,     color: "#3b82f6", testid: "nav-audit" },
];

export const HOSTEL_SUBNAV = [
  { to: "/hostel/upload",        label: "Загрузить фото",  icon: Upload,    color: "#f97316", testid: "nav-hostel-upload" },
  { to: "/hostel/registration",  label: "Прописка",        icon: MapPin,    color: "#10b981", testid: "nav-hostel-registration" },
  { to: "/hostel/documents",     label: "Документы",    icon: FolderArchive, color: "#f59e0b", testid: "nav-hostel-documents" },
  { to: "/hostel/residents",     label: "Все жильцы",      icon: Users,     color: "#3b82f6", testid: "nav-hostel-residents" },
  { to: "/hostel/hostels",       label: "Хостелы",         icon: Bed,       color: "#8b5cf6", testid: "nav-hostels" },
  { to: "/hostel/map",           label: "Планировка",      icon: Map,       color: "#14b8a6", testid: "nav-hostel-map" },
  { to: "/hostel/companies",     label: "Компании",        icon: Building2, color: "#06b6d4", testid: "nav-hostel-companies" },
  { to: "/hostel/counterparties", label: "Контрагенты",    icon: Handshake, color: "#ec4899", testid: "nav-hostel-counterparties" },
  { to: "/hostel/warehouse",     label: "Складской учёт",  icon: Warehouse, color: "#eab308", testid: "nav-hostel-warehouse" },
  { to: "/hostel/finance",       label: "Финансовый учёт", icon: Wallet,    color: "#10b981", testid: "nav-hostel-finance" },
  { to: "/hostel/contracts",     label: "Договоры",        icon: FileSignature, color: "#a855f7", testid: "nav-hostel-contracts" },
];

// Транспорт/Питание/Одежда — each is its own toggle-subnav module (like
// Табель/Хостел), not a single flat page: a tenant with the module owns a
// supply business with its own Заказы/Прайс-лист/Контрагенты/Финансы/
// Подключения — PLUS one section specific to that business: Транспорт gets
// a fleet (машины/водители/маршруты), Одежда gets a size-aware issuance
// ledger. Питание's recurring-заявка templates live inside its Заказы tab
// instead of a separate route (they're just a saved shortcut for that form).
export const TRANSPORT_SUBNAV = [
  { to: "/transport/orders",         label: "Заказы",       icon: ShoppingCart, color: "#f97316", testid: "nav-transport-orders" },
  { to: "/transport/tickets",        label: "Талоны",       icon: ClipboardList, color: "#0ea5e9", testid: "nav-transport-tickets" },
  { to: "/transport/fleet",          label: "Автопарк",     icon: Truck,        color: "#eab308", testid: "nav-transport-fleet" },
  { to: "/transport/reports",        label: "Отчёты",       icon: FileBarChart, color: "#22c55e", testid: "nav-transport-reports" },
  { to: "/transport/pricelist",      label: "Прайс-лист",   icon: Tag,          color: "#06b6d4", testid: "nav-transport-pricelist" },
  { to: "/transport/counterparties", label: "Контрагенты",  icon: Handshake,    color: "#ec4899", testid: "nav-transport-counterparties" },
  { to: "/transport/finance",        label: "Финансы",      icon: Wallet,       color: "#10b981", testid: "nav-transport-finance" },
  { to: "/transport/connections",    label: "Подключения",  icon: Link2,        color: "#8b5cf6", testid: "nav-transport-connections" },
];
export const FOOD_SUBNAV = [
  { to: "/food/orders",         label: "Заказы",       icon: ShoppingCart, color: "#f97316", testid: "nav-food-orders" },
  { to: "/food/quota",          label: "Предзаявка",   icon: ClipboardList, color: "#0ea5e9", testid: "nav-food-quota" },
  { to: "/food/tickets",        label: "Талоны",       icon: TicketIcon,   color: "#eab308", testid: "nav-food-tickets" },
  { to: "/food/reports",        label: "Отчёты",       icon: FileBarChart, color: "#22c55e", testid: "nav-food-reports" },
  { to: "/food/pricelist",      label: "Прайс-лист",   icon: Tag,          color: "#06b6d4", testid: "nav-food-pricelist" },
  { to: "/food/counterparties", label: "Контрагенты",  icon: Handshake,    color: "#ec4899", testid: "nav-food-counterparties" },
  { to: "/food/finance",        label: "Финансы",      icon: Wallet,       color: "#10b981", testid: "nav-food-finance" },
  { to: "/food/connections",    label: "Подключения",  icon: Link2,        color: "#8b5cf6", testid: "nav-food-connections" },
];
export const CLOTHING_SUBNAV = [
  { to: "/clothing/orders",         label: "Заказы",        icon: ShoppingCart, color: "#f97316", testid: "nav-clothing-orders" },
  { to: "/clothing/pricelist",      label: "Прайс-лист",    icon: Tag,          color: "#06b6d4", testid: "nav-clothing-pricelist" },
  { to: "/clothing/issuance",       label: "Учёт выдачи",   icon: Ruler,        color: "#0ea5e9", testid: "nav-clothing-issuance" },
  { to: "/clothing/counterparties", label: "Контрагенты",   icon: Handshake,    color: "#ec4899", testid: "nav-clothing-counterparties" },
  { to: "/clothing/finance",        label: "Финансы",       icon: Wallet,       color: "#10b981", testid: "nav-clothing-finance" },
  { to: "/clothing/connections",    label: "Подключения",   icon: Link2,        color: "#8b5cf6", testid: "nav-clothing-connections" },
];

// Магазин — офлайн-магазин/CRM: точки продаж, каталог, кассовые продажи,
// клиенты, поставщики. Not a provider_connections-style B2B module (no
// "Подключения" tab) — a standalone retail module. "/shop/import" (Chunk 8)
// is on hold pending a sample client file — not listed here yet.
export const SHOP_SUBNAV = [
  { to: "/shop/pos",       label: "Касса",         icon: ShoppingCart, color: "#f97316", testid: "nav-shop-pos" },
  { to: "/shop/sales",     label: "Продажи",       icon: Receipt,      color: "#0ea5e9", testid: "nav-shop-sales" },
  { to: "/shop/stock",     label: "Остатки",       icon: Warehouse,    color: "#eab308", testid: "nav-shop-stock" },
  { to: "/shop/purchases", label: "Закупки",       icon: Truck,        color: "#8b5cf6", testid: "nav-shop-purchases" },
  { to: "/shop/products",  label: "Каталог",       icon: Package,      color: "#06b6d4", testid: "nav-shop-products" },
  { to: "/shop/shifts",    label: "Смены",         icon: Clock,        color: "#22c55e", testid: "nav-shop-shifts" },
  { to: "/shop/customers", label: "Клиенты",       icon: Users,        color: "#3b82f6", testid: "nav-shop-customers" },
  { to: "/shop/suppliers", label: "Поставщики",    icon: Handshake,    color: "#ec4899", testid: "nav-shop-suppliers" },
  { to: "/shop/reports",   label: "Отчёты",        icon: FileBarChart, color: "#10b981", testid: "nav-shop-reports" },
  { to: "/shop/shops",     label: "Точки",         icon: Store,        color: "#14b8a6", testid: "nav-shop-shops" },
  { to: "/shop/settings",  label: "Настройки",     icon: Settings2,    color: "#64748b", testid: "nav-shop-settings" },
];

// Курьеры — личный модуль (не публичный SaaS-модуль, см. CLAUDE.md/сессию
// про перенос yandex/ реферальной программы). Маршрут /agents, НЕ /couriers —
// /couriers зарезервирован под отдельно авторизуемый портал самих курьеров
// (CourierPortal*, свой роутинг вне Layout, см. App.js).
// Привлечение — отдел, который ищет людей: рабочих, жильцов и покупателей.
// Лид живёт здесь только до конвертации, дальше человек уходит в свой модуль
// (Табель / Хостел / Магазин), см. leadgen-блок в server.py.
export const LEADGEN_SUBNAV = [
  { to: "/leadgen/dashboard", label: "Дашборд",  icon: LayoutDashboard, color: "#6366f1", testid: "nav-leadgen-dashboard" },
  { to: "/leadgen/leads",     label: "Лиды",      icon: Users,           color: "#3b82f6", testid: "nav-leadgen-leads" },
  { to: "/leadgen/forms",     label: "Ссылки",    icon: Link2,           color: "#8b5cf6", testid: "nav-leadgen-forms" },
  { to: "/leadgen/landings",  label: "Лендинги",  icon: BuildingIcon,    color: "#14b8a6", testid: "nav-leadgen-landings" },
  { to: "/leadgen/messages",  label: "Сообщения", icon: Inbox,           color: "#eab308", testid: "nav-leadgen-messages" },
  { to: "/leadgen/channels",  label: "Каналы",    icon: Radio,           color: "#f97316", testid: "nav-leadgen-channels" },
  { to: "/leadgen/reports",   label: "Отчёты",    icon: FileBarChart,    color: "#10b981", testid: "nav-leadgen-reports" },
];

export const AGENTS_SUBNAV = [
  { to: "/agents/dashboard", label: "Дашборд",  icon: LayoutDashboard, color: "#f97316", testid: "nav-agents-dashboard" },
  { to: "/agents/submissions", label: "Заявки", icon: Inbox,           color: "#06b6d4", testid: "nav-agents-submissions" },
  { to: "/agents/leads",     label: "Лиды",      icon: Users,           color: "#3b82f6", testid: "nav-agents-leads" },
  { to: "/agents/couriers",  label: "Курьеры",   icon: UserCog,         color: "#8b5cf6", testid: "nav-agents-couriers" },
  { to: "/agents/payouts",   label: "Выплаты",   icon: Wallet,          color: "#10b981", testid: "nav-agents-payouts" },
  { to: "/agents/rates",     label: "Ставки",    icon: Percent,         color: "#eab308", testid: "nav-agents-rates" },
  { to: "/agents/import",    label: "Импорт",    icon: Upload,          color: "#06b6d4", testid: "nav-agents-import" },
  { to: "/agents/settings",  label: "Настройки", icon: Settings2,       color: "#64748b", testid: "nav-agents-settings" },
];

function getActiveModuleForPath(pathname) {
  if (pathname.startsWith("/leadgen")) return "leadgen";
  if (pathname.startsWith("/hostel")) return "hostel";
  if (pathname.startsWith("/transport")) return "transport";
  if (pathname.startsWith("/food")) return "food";
  if (pathname.startsWith("/clothing")) return "clothing";
  if (pathname.startsWith("/shop")) return "shop";
  if (pathname.startsWith("/agents")) return "couriers";
  if (pathname === "/home" || pathname === "/") return null;
  // /notifications, /finance, /support — общие страницы, не принадлежат модулю
  if (pathname === "/notifications" || pathname.startsWith("/notifications/")) return null;
  if (pathname === "/finance" || pathname.startsWith("/finance/")) return null;
  if (pathname === "/support" || pathname.startsWith("/support/")) return null;
  const tabelPaths = [
    "/manager/dashboard", "/upload", "/export", "/employees", "/candidates", "/professions",
    "/enterprises", "/fines", "/deductions", "/bonuses", "/rentability",
    "/daily-report", "/audit", "/import", "/dashboard", "/timesheets",
  ];
  for (const p of tabelPaths) {
    if (pathname === p || pathname.startsWith(p + "/")) return "tabel";
  }
  return null;
}

// ─── Sub-nav item ─────────────────────────────────────────────────────────────

function SubNavItem({ item, badge, onClick }) {
  const IconComp = item.icon;
  return (
    <NavLink
      to={item.to}
      end={false}
      data-testid={item.testid}
      onClick={onClick}
      className={({ isActive }) => cn("sub-nav-link", isActive && "is-active")}
    >
      <span
        className="sub-nav-icon-badge"
        style={{ background: item.color + "22", border: `1.5px solid ${item.color}44` }}
      >
        <IconComp className="w-3.5 h-3.5" style={{ color: item.color }} />
      </span>
      <span className="flex-1 truncate text-sm">{item.label}</span>
      {badge != null && badge > 0 && (
        <span className="notif-badge" data-testid="notif-badge">{badge}</span>
      )}
    </NavLink>
  );
}

// ─── Rail button ──────────────────────────────────────────────────────────────

function RailBtn({ icon: Icon, label, onClick, isActive, testid }) {
  return (
    <button
      type="button"
      className={cn("rail-btn", isActive && "is-active")}
      onClick={onClick}
      data-testid={testid}
      aria-label={label}
      title={label}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
}

function RailNavBtn({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => cn("rail-btn", isActive && "is-active")}
      data-testid={testid}
      aria-label={label}
      title={label}
    >
      <Icon className="w-5 h-5" />
    </NavLink>
  );
}

function BrandArchiveCard({ compact = false }) {
  return (
    <div className={cn("brand-archive-card", compact && "brand-archive-card--compact")}>
      <div className="brand-archive-card__mark">
        <span aria-label="RSU">
          <b>R</b><b>S</b><b>U</b>
        </span>
      </div>
      {!compact && (
        <div className="brand-archive-card__text">
          <span className="brand-archive-card__title">Архив</span>
          <span className="brand-archive-card__subtitle">объекты (23)</span>
        </div>
      )}
    </div>
  );
}

// ─── Main Layout ──────────────────────────────────────────────────────────────

export default function Layout({ user, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const [limits, setLimits]         = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settings, setSettings]     = useState(null);
  const [notifCount, setNotifCount] = useState(0);
  const [supportUnread, setSupportUnread] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openModule, setOpenModule] = useState(() => getActiveModuleForPath(location.pathname));

  const modules      = limits?.modules || user?.effective_modules || user?.modules || { tabel: false, hostel: false, transport: false, food: false, clothing: false, shop: false, couriers: false, leadgen: false };
  const showHostel   = !!modules.hostel;
  const showTabel    = !!modules.tabel;
  const showTransport = !!modules.transport;
  const showFood     = !!modules.food;
  const showClothing = !!modules.clothing;
  const showShop     = !!modules.shop;
  const showCouriers = !!modules.couriers;
  const showLeadgen  = !!modules.leadgen;
  const isSuperAdmin = limits?.is_super_admin || user?.role === "super_admin" || user?.role === "admin";
  const isCompanyAdmin = isSuperAdmin || limits?.is_company_admin || user?.role === "company_admin";

  useEffect(() => {
    // Neither call depends on the current route — fetch once when the
    // layout mounts (login) instead of on every navigation, which used to
    // add two extra round trips per page change for no reason.
    authApi.limits().then(({ data }) => { setLimits(data); setAccessLimits(data); }).catch(() => {});
    authApi.settings().then(({ data }) => {
      setSettings(data);
      if (data?.theme === "light" || data?.theme === "dark") {
        document.documentElement.dataset.theme = data.theme;
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const m = getActiveModuleForPath(location.pathname);
    if (m) setOpenModule(m);
    else if (location.pathname === "/home") setOpenModule(null);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      // Skip the network round trip while the tab is backgrounded — a
      // notification count nobody can see isn't worth a DB query.
      if (document.visibilityState === "hidden") return Promise.resolve();
      return import("@/lib/api").then(({ notificationsApi, supportApi }) => Promise.all([
        notificationsApi.all()
          .then(({ data }) => { if (!cancelled) setNotifCount(data.count || 0); })
          .catch(() => {}),
        supportApi.unreadCount()
          .then(({ data }) => { if (!cancelled) setSupportUnread(data.count || 0); })
          .catch(() => {}),
      ]));
    };
    load();
    const t = setInterval(load, 60_000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { cancelled = true; clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, []); // runs once on mount, polls every minute while the tab is visible

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleToggleTheme = () => {
    toggleTheme();
    if (settings) {
      const next = theme === "dark" ? "light" : "dark";
      authApi.updateSettings({ ...settings, theme: next }).catch(() => {});
    }
  };

  const logout = () => {
    localStorage.removeItem("tabel_token");
    localStorage.removeItem("tabel_user");
    localStorage.removeItem("tabel_limits");
    navigate("/login");
  };

  const toggleModule = (mod) => {
    if (openModule === mod) {
      if (getActiveModuleForPath(location.pathname) === mod) navigate("/home");
      setOpenModule(null);
    } else {
      setOpenModule(mod);
    }
  };

  const hiddenNav = settings?.tabs || {};
  // null = unrestricted (no custom role assigned to this account — today's
  // behavior). An object narrows each module to exactly these tab paths,
  // see MODULE_TABS/company_roles on the backend.
  const allowedTabs = limits?.allowed_tabs ?? null;
  const SUBNAV_BY_MODULE = {
    tabel: TABEL_SUBNAV,
    hostel: HOSTEL_SUBNAV,
    transport: TRANSPORT_SUBNAV,
    food: FOOD_SUBNAV,
    clothing: CLOTHING_SUBNAV,
    shop: SHOP_SUBNAV,
    couriers: AGENTS_SUBNAV,
    leadgen: LEADGEN_SUBNAV,
  };
  const rawSubnav = SUBNAV_BY_MODULE[openModule] || [];
  const roleAllowedForModule = allowedTabs && openModule ? (allowedTabs[openModule] || []) : null;
  const activeSubnav = rawSubnav.filter((item) =>
    hiddenNav[item.to] !== false && (!roleAllowedForModule || roleAllowedForModule.includes(item.to))
  );
  const isSubPanelOpen = openModule !== null && activeSubnav.length > 0;

  // A role-restricted account landing directly on a tab's URL (typed, an
  // old bookmark, a hidden nav link) — real enforcement lives on the
  // backend (every gated endpoint 403s), this just avoids showing a blank/
  // broken page for a tab the nav never would have offered.
  useEffect(() => {
    if (!limits) return; // wait for the one fetch on mount before judging
    if (!allowedTabs) return;
    const m = getActiveModuleForPath(location.pathname);
    if (!m) return;
    const allowedForModule = allowedTabs[m] || [];
    const subnav = SUBNAV_BY_MODULE[m] || [];
    const matchedTab = subnav.find(
      (item) => location.pathname === item.to || location.pathname.startsWith(item.to + "/")
    );
    if (matchedTab && !allowedForModule.includes(matchedTab.to)) {
      toast.error("Нет доступа к этому разделу — обратитесь к администратору компании");
      navigate("/home", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limits, location.pathname]);

  // notifBadge не нужен — уведомления теперь в рейле, не в subpanel
  const notifBadge = (_item) => undefined;

  return (
    <div className="app-shell h-screen flex overflow-hidden">
      {/* Режим «должник». Монтируется здесь, а не в App: под Layout ходят
          только авторизованные владельцы и их сотрудники, а у порталов
          курьера и жильца своя авторизация и свой токен. */}
      <DebtorBlockGate />

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-backdrop"
        />
      )}

      {/* ══ SIDEBAR: Rail + Sub-panel ══ */}
      <aside
        className={cn(
          "sidebar-shell sticky top-0 h-screen z-40 flex flex-shrink-0",
          "transition-transform duration-300 ease-in-out",
          /* mobile: off-screen by default, slides in when open */
          "max-md:fixed max-md:inset-y-0 max-md:left-0",
          mobileOpen ? "translate-x-0" : "max-md:-translate-x-full",
        )}
      >
        {/* ── Icon Rail (60px) ── */}
        <div className="rail flex flex-col items-center py-3 shrink-0">
          {/* Brand */}
          <div className="mb-3">
            <BrandArchiveCard compact />
          </div>

          <nav className="flex flex-col items-center gap-1 flex-1 w-full px-2">
            <RailNavBtn to="/home" icon={Home} label="Главное меню" testid="nav-home" />
            <RailNavBtn to="/subscription" icon={Crown} label="Тарифы" testid="nav-subscription" />
            <button
              type="button"
              className="rail-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="Глобальный поиск"
              title="Глобальный поиск (Ctrl+K)"
              data-testid="nav-global-search"
            >
              <Search className="w-5 h-5" />
            </button>

            {showTabel && (
              <RailBtn
                icon={ClipboardList}
                label="Предприятия"
                isActive={openModule === "tabel"}
                onClick={() => toggleModule("tabel")}
                testid="nav-rail-tabel"
              />
            )}

            {showHostel && (
              <RailBtn
                icon={Bed}
                label="Хостел"
                isActive={openModule === "hostel"}
                onClick={() => toggleModule("hostel")}
                testid="nav-rail-hostel"
              />
            )}

            {(showTabel || showHostel) && (showTransport || showFood || showClothing) && (
              <div className="rail-divider" />
            )}

            {showTransport && (
              <RailBtn
                icon={CarTaxiFront}
                label="Транспорт"
                isActive={openModule === "transport"}
                onClick={() => toggleModule("transport")}
                testid="nav-rail-transport"
              />
            )}
            {showFood && (
              <RailBtn
                icon={Utensils}
                label="Питание"
                isActive={openModule === "food"}
                onClick={() => toggleModule("food")}
                testid="nav-rail-food"
              />
            )}
            {showClothing && (
              <RailBtn
                icon={Shirt}
                label="Одежда"
                isActive={openModule === "clothing"}
                onClick={() => toggleModule("clothing")}
                testid="nav-rail-clothing"
              />
            )}

            {(showTransport || showFood || showClothing) && showShop && (
              <div className="rail-divider" />
            )}

            {showShop && (
              <RailBtn
                icon={Store}
                label="Магазин"
                isActive={openModule === "shop"}
                onClick={() => toggleModule("shop")}
                testid="nav-rail-shop"
              />
            )}

            {showCouriers && (
              <RailBtn
                icon={Bike}
                label="Курьеры"
                isActive={openModule === "couriers"}
                onClick={() => toggleModule("couriers")}
                testid="nav-rail-couriers"
              />
            )}

            {showLeadgen && (
              <RailBtn
                icon={Megaphone}
                label="Привлечение"
                isActive={openModule === "leadgen"}
                onClick={() => toggleModule("leadgen")}
                testid="nav-rail-leadgen"
              />
            )}

            <div className="rail-divider" />

            {/* Общие финансы — единая для всех модулей, не привязана к конкретному;
                видна только главному админу и админу компании, см. GET /finance/overview */}
            {isCompanyAdmin && (
              <RailNavBtn to="/finance" icon={Landmark} label="Общие финансы" testid="nav-finance-overview" />
            )}

            {/* Notifications — единая для всех модулей, перед настройками */}
            <NavLink
              to="/notifications"
              className={({ isActive }) => cn("rail-btn relative", isActive && "is-active")}
              data-testid="nav-notifications"
              aria-label="Уведомления"
              title="Уведомления"
            >
              <Bell className="w-5 h-5" />
              {notifCount > 0 && (
                <span
                  className="absolute top-1 right-1 min-w-[14px] h-[14px] rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                  style={{ background: "#ef4444", lineHeight: 1 }}
                >
                  {notifCount > 99 ? "99+" : notifCount}
                </span>
              )}
            </NavLink>

            {/* Поддержка — чат с главным администратором: обычное обращение,
                баг или предложение по улучшению (см. /support) */}
            <NavLink
              to="/support"
              className={({ isActive }) => cn("rail-btn relative", isActive && "is-active")}
              data-testid="nav-support"
              aria-label="Поддержка"
              title="Поддержка"
            >
              <LifeBuoy className="w-5 h-5" />
              {supportUnread > 0 && (
                <span
                  className="absolute top-1 right-1 min-w-[14px] h-[14px] rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                  style={{ background: "#ef4444", lineHeight: 1 }}
                >
                  {supportUnread > 99 ? "99+" : supportUnread}
                </span>
              )}
            </NavLink>

            <RailNavBtn to="/settings" icon={SettingsIcon} label="Настройки" testid="nav-settings" />
            {isCompanyAdmin && (
              <RailNavBtn to="/admin" icon={ShieldCheck} label="Админ-панель" testid="nav-admin" />
            )}
          </nav>

          {/* Theme + logout at bottom */}
          <div className="flex flex-col items-center gap-1 w-full px-2 pb-1">
            <button
              type="button"
              className="rail-btn"
              onClick={handleToggleTheme}
              aria-label="Переключить тему"
              title={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              type="button"
              className="rail-btn rail-btn--danger"
              onClick={logout}
              aria-label="Выйти"
              title="Выйти"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Sub-panel (220px, slides in) ── */}
        <div className={cn("sub-panel flex flex-col", isSubPanelOpen ? "sub-panel--open" : "sub-panel--closed")}>
          {/* Header */}
          <div className="sub-panel-header">
            <button
              type="button"
              className="md:hidden rail-btn flex-shrink-0"
              onClick={() => setMobileOpen(false)}
              aria-label="Закрыть"
              data-testid="mobile-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Nav items */}
          <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 space-y-0.5">
            {activeSubnav.map((item) => (
              <SubNavItem
                key={item.to}
                item={item}
                badge={notifBadge(item)}
                onClick={() => setMobileOpen(false)}
              />
            ))}
          </nav>

          {/* Подписка и квота распознаваний */}
          {limits && openModule && (
            <div className="mx-2 mb-2 px-3 py-2.5 rounded-xl glass-panel text-xs">
              <div className="flex items-center gap-1 mb-1"
                   style={{ color: "var(--foreground-3)", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                <Zap className="w-2.5 h-2.5" />
                Распознавания
              </div>
              {limits.extracts_limit !== null ? (
                <div style={{ color: "var(--foreground-2)" }}>
                  Осталось{" "}
                  <span className="font-semibold"
                        style={{ color: limits.extracts_remaining === 0 ? "var(--danger)" : "var(--success)" }}>
                    {limits.extracts_remaining}
                  </span>
                  {" "}/{" "}{limits.extracts_limit} в месяц
                </div>
              ) : (
                <div className="font-medium" style={{ color: "var(--success)" }}>Безлимит</div>
              )}

              {/* Состояние подписки. Показываем только когда есть что сказать:
                  постоянная плашка «всё хорошо» — визуальный шум. */}
              {limits.billing?.blocked?.length > 0 && (
                <div className="mt-1.5 font-medium" style={{ color: "var(--danger)" }}>
                  Только чтение: {limits.billing.blocked.map((b) => b.name).join(", ")}
                </div>
              )}
              {!limits.billing?.blocked?.length && limits.billing?.trial && (
                <div className="mt-1.5" style={{ color: "var(--foreground-2)" }}>
                  Пробный период:{" "}
                  <span className="font-semibold" style={{ color: limits.billing.trial_days_left <= 3 ? "var(--danger)" : "var(--accent)" }}>
                    {limits.billing.trial_days_left} дн.
                  </span>
                </div>
              )}
              {!limits.billing?.blocked?.length && !limits.billing?.trial
                && limits.billing?.expiring && limits.billing.expiring.days_left <= 7 && (
                <div className="mt-1.5" style={{ color: "var(--warning, #f59e0b)" }}>
                  «{limits.billing.expiring.name}» — {limits.billing.expiring.days_left} дн.
                </div>
              )}

              {!limits.billing?.is_admin && (
                <NavLink to="/subscription" className="mt-1 block text-[11px] font-medium" style={{ color: "var(--accent)" }}>
                  {limits.billing?.blocked?.length ? "Продлить подписку →" : "Управление подпиской →"}
                </NavLink>
              )}
            </div>
          )}

          {/* User info */}
          <div className="px-2 py-2.5" style={{ borderTop: "1px solid var(--sidebar-border)" }}>
            <div className="px-3 py-2 rounded-xl" style={{ background: "var(--glass-bg)" }}>
              <div className="text-[10px] truncate" style={{ color: "var(--foreground-3)" }} data-testid="user-email">
                {user?.email}
              </div>
              <div className="text-xs font-medium flex items-center gap-1 truncate" style={{ color: "var(--foreground)" }}>
                {user?.name}
                {isSuperAdmin && <ShieldCheck className="w-3 h-3 flex-shrink-0" style={{ color: "var(--warning)" }} />}
              </div>
              {user?.company?.name && (
                <div className="text-[10px] mt-0.5 truncate" style={{ color: "var(--foreground-3)" }}>
                  🏢 {user.company.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile topbar */}
        <header
          className="md:hidden sticky top-0 z-20 px-3 py-2.5 flex items-center gap-3"
          style={{
            background: "var(--sidebar-bg)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderBottom: "1px solid var(--sidebar-border)",
          }}
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-lg"
            style={{ color: "var(--foreground-2)" }}
            data-testid="mobile-open"
            aria-label="Открыть меню"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <BrandArchiveCard compact />
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 md:p-6 lg:p-8 max-w-[1600px] w-full animate-fadeUp">
          <div className="flex justify-end mb-3">
            <PwaInstallButton />
          </div>
          {children}
        </div>
      </main>

      {/* Нейрохам — развлекательный бот поверх всех страниц. Знает, на какой
          вкладке человек стоит, но никаких данных не читает: только шутит и
          кидает ссылку на нужный раздел. */}
      <NeurohamWidget />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
